import {
  IEmployeeAppArchivedInstance,
  maximizeInstanceProgress
} from '@dadohr/shared/domainTypes/experience';
import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DialogActionsWithSlots } from '../../../../../../../components/DialogActionsWithSlots';
import {
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { TableSearch } from '../../../../../../../components/Table';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import {
  toFullName,
  toSearchTermRegexp
} from '../../../../../../../services/user';
import { useProductAnalyticsV2 } from '../../../../../../features/Experience/services/productAnalytics';
import { ROUTES_V2 } from '../../../../../../routes';
import { EmployeeAppPageLoader } from '../../../components/EmployeeAppLoader';
import { ProgressTracker } from '../../../components/ProgressTracker';
import { useOtherArchivedInstances } from '../../../services/archivedInstances';

type ColumnName =
  | 'template'
  | 'role'
  | 'participant'
  | 'start'
  | 'end'
  | 'progress'
  | 'actions';
type D = IEmployeeAppArchivedInstance;
type OtherProps = {
  userAccessToken: string;
};
type Column = IColumn<D, ColumnName, OtherProps>;

const ROW_TO_KEY = (d: D) => d.instanceAccessToken;
const ROW_TO_HREF = (d: D, o: OtherProps) => {
  return ROUTES_V2.instance.url(o.userAccessToken, d.instanceAccessToken);
};

const Actions = ({ d, o }: { d: D; o: OtherProps }) => {
  const { t } = useUnsafeTranslation();
  return (
    <Link to={ROW_TO_HREF(d, o)}>
      <Button variant="text" color="primary" size="small">
        {t('View experience')}
      </Button>
    </Link>
  );
};

const COLUMNS: Column[] = [
  {
    key: 'template',
    head: () => 'Experience',
    cell: (d) => d.templateTitle,
    align: 'left',
    sortable: true,
    width: 250,
    flexGrow: 3,
    noWrap: true
  },
  {
    key: 'participant',
    head: () => 'Employee name',
    cell: (d) => toFullName(d.participant),
    align: 'left',
    width: 150,
    sortable: true,
    flexGrow: 1,
    noWrap: true
  },
  {
    key: 'role',
    head: () => 'Your role',
    cell: (d) => d.actor.label,
    align: 'left',
    width: 150,
    sortable: true,
    flexGrow: 1,
    noWrap: true
  },
  {
    key: 'progress',
    head: () => 'Progress',
    cell: (d) => (
      <div style={{ width: '100%' }}>
        <ProgressTracker
          d={maximizeInstanceProgress(d.progress)}
          stretch
          noTitle
          percentageVariant="body2"
          barMaxWidth={150}
        />
      </div>
    ),
    sortable: true,
    align: 'left',
    width: 220,
    flexGrow: 0,
    noWrap: true
  },
  {
    key: 'start',
    head: () => 'Start date',
    cell: (d) => moment(d.range.start).format('MMM D, YYYY'),
    align: 'right',
    width: 150,
    sortable: true,
    flexGrow: 0,
    noWrap: true
  },
  {
    key: 'end',
    head: () => 'Archive date',
    cell: (d) => moment(d.range.end).format('MMM D, YYYY'),
    align: 'right',
    width: 150,
    sortable: true,
    flexGrow: 0,
    noWrap: true
  },
  {
    key: 'actions',
    head: () => 'Actions',
    cell: (d, o) => <Actions d={d} o={o} />,
    align: 'right',
    width: 150,
    flexGrow: 0,
    noWrap: true
  }
];

const SORTERS: ItemSorters<D, OtherProps> = {
  template: {
    key: 'template',
    items: { sort: (d) => d.templateTitle, dir: 'asc' }
  },
  role: {
    key: 'role',
    items: { sort: (d) => d.actor.label, dir: 'asc' }
  },
  progress: {
    key: 'progress',
    items: { sort: (d) => d.progress?.p || 0, dir: 'desc' }
  },
  participant: {
    key: 'participant',
    items: { sort: (d) => toFullName(d.participant), dir: 'asc' }
  },
  start: {
    key: 'start',
    items: { sort: (d) => d.range.start, dir: 'desc' }
  },
  end: {
    key: 'end',
    items: { sort: (d) => d.range.end, dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.end;

const ArchivedOtherInstancesTable = ({
  data,
  userAccessToken
}: {
  data: IEmployeeAppArchivedInstance[];
  userAccessToken: string;
}) => {
  const [[sorter, dir], setSort] = useSortQueryParam('aSort', SORTERS);
  const { trackEventV2 } = useProductAnalyticsV2(userAccessToken);
  const otherProps = useMemo<OtherProps>(() => {
    return {
      userAccessToken
    };
  }, [userAccessToken]);

  const sorterOrDefault = sorter || DEFAULT_SORTER;
  return (
    <RowsRenderer
      fullWidth
      variant="contained"
      columns={COLUMNS}
      rows={data}
      rowToKey={ROW_TO_KEY}
      rowToHref={ROW_TO_HREF}
      onRowClick={() => {
        trackEventV2({
          eventType: PAEventTypeV2.OTHER,
          name: 'Dashboard Experience Card Clicked',
          Explanation: 'User clicked on a card on the dashboard',
          'Role in Experience': 'Supporting Actor',
          Archived: 'Yes'
        });
      }}
      sorter={sorterOrDefault}
      sortDirection={dir}
      renderHead={true}
      onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
      chunkSize={75}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};

export const ArchivedOtherInstances = ({
  open,
  onClose,
  userAccessToken
}: {
  open: boolean;
  onClose: () => void;
  userAccessToken: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [data] = useOtherArchivedInstances(userAccessToken);
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    if (!data || !q) {
      return data;
    }
    const re = toSearchTermRegexp(q);
    return data.filter((d) => {
      return re.test(d.templateTitle) || re.test(toFullName(d.participant));
    });
  }, [data, q]);
  return (
    <Dialog open={open} onClose={onClose} fullScreen fullWidth>
      <DialogTitle>
        <FlexContainer fullWidth justifyContent="space-between">
          <Typography variant="h4">
            {t(`Experiences you were a part of`)}
          </Typography>
          <div style={{ width: '328px' }}>
            <TableSearch
              term={q}
              onChange={setQ}
              autoFocus
              placeholder={t('Search by experience or employee name')}
              fullWidth
            />
          </div>
        </FlexContainer>
      </DialogTitle>
      <DialogContent>
        {!rows && <EmployeeAppPageLoader />}
        {rows && (
          <ArchivedOtherInstancesTable
            data={rows}
            userAccessToken={userAccessToken}
          />
        )}
      </DialogContent>
      <DialogActionsWithSlots
        right={<Button onClick={onClose}>{t('Close')}</Button>}
      />
    </Dialog>
  );
};
